import { ThemeBuilder } from './ThemeBuilder';

/**
 * Public re-export of `ThemeBuilder.defineTheme` — the single authoring entry
 * point for a Blufin theme.
 * ## Token taxonomy at a glance
 * The engine emits two layers per mode:
 * **Foundation** (140 tokens per mode, see `tokens/Foundation/Foundation.ts`):
 *  - 21 gray stops (`--gray-0` … `--gray-1000`)
 *  - 6 color families × 19 stops (`--brand-*`, `--accent-*`, `--success-*`,
 *    `--warning-*`, `--danger-*`, `--info-*`)
 *  - 5 shadow primitives (`--shadow-xs` … `--shadow-xl`)
 *  - **Primitive scales** (mode-invariant, emitted at `:root`): typography,
 *    spacing, motion, z-index — see `tokens/{Typography,Spacing,Motion,ZIndex}`.
 * **Semantic** (211 tokens per mode, see `tokens/Semantic/Semantic.ts`):
 *  - text — `--text-primary`, `--text-on-{brand,accent,success,warning,danger,info,sidebar}`, …
 *  - surface — `--surface-{canvas,default,raised,overlay,sunken,sidebar,hover,hover-brand,selected,pressed}`
 *  - sidebar context — `--nav-item-*`, `--text-on-sidebar`, `--border-on-sidebar`
 *  - border — `--border-{subtle,default,strong,divider,on-brand,on-feedback}`
 *  - focus — `--ring-focus*` (use as `outline`, not box-shadow)
 *  - action — 8 roles × 11 slots
 *  - accent — `--accent-{bg,bg-subtle,fg,on-bg,border}`
 *  - brand — `--brand-{bg,bg-subtle,fg,on-bg,border}`
 *  - feedback — 4 roles × 5 slots: `--feedback-<role>-{bg,bg-subtle,fg,on-bg,border}`
 *  - shadow — `--shadow-{card,raised,overlay,popover,modal,tooltip,inset}`
 *  - chart — categorical (1..8), sequential (1..5), diverging (1..5),
 *    positive/negative, grid/axis/tooltip
 *  - icon — `--icon-{default,muted,on-brand,on-accent,on-feedback,disabled}`
 *  - state — `--state-disabled-{bg,fg,border}` (shared by every action role's disabled slots)
 * **Radius role tokens** (mode-invariant): `--radius`, `--radius-{card,control,input,pill,tooltip,none}` —
 * derived from the theme's `radius` preset.
 * **Density** (cascade-driven via `.density-{compact,standard,spacious}`):
 * `--density-row-height` and other density-aware sizing — see `tokens/Density`.
 * @example
 *   export const myTheme = defineTheme({
 *     name: 'my-theme',
 *     brand: '#3b82f6',
 *     accent: '#a855f7',
 *     gray: 'cool',
 *     radius: 'md',
 *     defaultMode: 'system',
 *   });
 */
export const defineTheme = ThemeBuilder.defineTheme;
